import { macroEnergyGap, macroEnergyPercentage } from '@/utils/macro-energy';
import { formatStructuredClinicalLimit } from './analysis/clinicalTargetSemantics';
import React from 'react';
import { Box, Button, Chip, Paper, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { Client } from '@/types/client';
// Temporarily hidden: meal slot configuration on client details.
// import { MealTypesIntegrated } from '@/components/clients/meal-settings/MealTypesIntegrated';
import { formatDisplayLabel } from '@/utils/display-labels';
import { findDrvRequirementByNutrientIds } from '@/utils/drv-resolve-primary-target';
import { colors, formatMetric } from './analysis/analysis.shared';
import {
  getClientClinicalCalorieAdjustmentInfo,
  type ClientClinicalCalorieAdjustmentInfo,
} from './clientDetailsV2.utils';

interface ClientDetailsV2ClientTargetsTabProps {
  client: Client;
  onOpenClientGoalsEditor?: (anchorEl: HTMLElement) => void;
}

type ClientGoalRecord = Record<string, unknown>;

type TargetRow = {
  label: string;
  target: string;
  energy?: string;
  range?: string;
  note?: string;
  tooltip?: React.ReactNode;
  calculationExplanation?: string;
};

type ClinicalTargetKey =
  | 'targetProtein'
  | 'targetCarbs'
  | 'targetFat'
  | 'targetSaturatedFat'
  | 'targetFibre'
  | 'targetSodium'
  | 'targetFreeSugars';

type MicroTargetRow = {
  label: string;
  target: string;
  valueSources: string[];
  valueRules: string[];
  prioritySources: string[];
  priorityRules: string[];
  priorityOnly: boolean;
  hasNumericTarget: boolean;
};

function asRecord(value: unknown): ClientGoalRecord | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as ClientGoalRecord)
    : null;
}

function finiteNumber(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatNumber(value: unknown, decimals = 0): string | null {
  const n = finiteNumber(value);
  if (n == null) return null;
  return decimals > 0 && Math.abs(n % 1) > 0
    ? n.toFixed(decimals).replace(/\.?0+$/, '')
    : formatMetric(n, 0);
}

function normalizeUnit(unit: unknown, fallback: string): string {
  const raw = String(unit ?? '').trim();
  return raw || fallback;
}

function formatValue(value: unknown, unit: unknown, fallbackUnit: string, decimals = 0): string | null {
  const formatted = formatNumber(value, decimals);
  if (!formatted) return null;
  const resolvedUnit = normalizeUnit(unit, fallbackUnit);
  if (resolvedUnit === '%') return `${formatted}%`;
  if (resolvedUnit === '% E') return `${formatted}% E`;
  return `${formatted} ${resolvedUnit}`;
}

function formatRange(range: unknown): string | null {
  const r = asRecord(range);
  if (!r) return null;
  const min = finiteNumber(r.min);
  const max = finiteNumber(r.max);
  const unit = normalizeUnit(r.unit, '');

  if (min == null && max == null) return null;
  if (min != null && max != null) {
    if (min === 0 && max > 0) return `<= ${formatMetric(max, 0)} ${unit}`.trim();
    if (min === max) return `${formatMetric(min, 0)} ${unit}`.trim();
    return `${formatMetric(min, 0)}-${formatMetric(max, 0)} ${unit}`.trim();
  }
  if (min != null) return `>= ${formatMetric(min, 0)} ${unit}`.trim();
  return `<= ${formatMetric(max ?? 0, 0)} ${unit}`.trim();
}

function macroRow(
  goals: ClientGoalRecord,
  label: string,
  key: string,
  fallbackUnit = 'g',
  clinicalTargetKey?: ClinicalTargetKey,
  fallbackCalculationExplanation?: string,
  calorieTarget?: number | null,
): TargetRow | null {
  const goal = asRecord(goals[key]);
  if (!goal) return null;
  const grams = asRecord(goal.grams);
  const kcal = asRecord(goal.kcal);
  const percent = asRecord(goal.energyPercent);
  const primary =
    formatValue(grams?.value ?? goal.value, grams?.unit ?? goal.unit, fallbackUnit, 1)
    ?? formatValue(goal.value, goal.unit, fallbackUnit);

  if (!primary) return null;

  const gramAmount = finiteNumber(grams?.value);
  const factor = key === 'totalFat' || key === 'saturatedFat' ? 9 : 4;
  const percentText = calorieTarget && gramAmount != null
    ? `${macroEnergyPercentage(gramAmount * factor, calorieTarget).toFixed(1)}% E`
    : formatValue(percent?.value, percent?.unit, '% E', 1);
  const energy = formatValue(kcal?.value, kcal?.unit, 'kcal');
  const limit = (getResolvedClinicalTargets(goals).limits as any)?.[key === 'totalFat' ? 'fat' : key === 'carbohydrates' ? 'carbs' : key];
  const range = formatStructuredClinicalLimit(limit) ?? formatRange(goal.clinicalRange);

  const calculationExplanation = String(goal.calculationExplanation ?? '').trim() || fallbackCalculationExplanation;
  return withClinicalTargetTooltip(goals, clinicalTargetKey, {
    label,
    target: primary,
    energy: energy ?? percentText ?? undefined,
    range: range ?? undefined,
    note: percentText && energy ? percentText : undefined,
    calculationExplanation,
    tooltip: calculationExplanation ? renderTargetCalculationTooltip(calculationExplanation) : undefined,
  });
}

function scalarRow(
  goals: ClientGoalRecord,
  label: string,
  key: string,
  fallbackUnit: string,
  decimals = 0,
  clinicalTargetKey?: ClinicalTargetKey,
  fallbackCalculationExplanation?: string,
): TargetRow | null {
  const goal = asRecord(goals[key]);
  if (!goal) return null;
  const target = formatValue(goal.value, goal.unit, fallbackUnit, decimals);
  if (!target) return null;

  const calculationExplanation = String(goal.calculationExplanation ?? '').trim() || fallbackCalculationExplanation;
  return withClinicalTargetTooltip(goals, clinicalTargetKey, {
    label,
    target: key === 'dietaryFibre' ? `≥${target}` : ['sodium', 'freeSugars'].includes(key) ? `≤${target}` : target,
    range: formatStructuredClinicalLimit((getResolvedClinicalTargets(goals).limits as any)?.[key === 'dietaryFibre' ? 'fibre' : key]) ?? formatRange(goal.clinicalRange) ?? undefined,
    calculationExplanation,
    tooltip: calculationExplanation ? renderTargetCalculationTooltip(calculationExplanation) : undefined,
  });
}

function renderTargetCalculationTooltip(explanation: string): React.ReactNode {
  return (
    <Box sx={{ display: 'grid', gap: 0.6, maxWidth: 460 }}>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>
        Target Calculation
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', lineHeight: 1.5, color: '#fff' }}>
        {explanation}
      </Typography>
    </Box>
  );
}

function resolveDailyCalorieTarget(client: Client, goals: ClientGoalRecord): number | null {
  const clinicalAdjustment = getClientClinicalCalorieAdjustmentInfo(client);
  if (clinicalAdjustment) return clinicalAdjustment.adjustedCalories;

  const clientRecord = client as unknown as ClientGoalRecord;
  const goalCalories = asRecord(goals.calories);
  const goalCaloriesKcal = asRecord(goalCalories?.kcal);
  const clinicalRules = asRecord(goals.clinicalRules);
  const resolvedTargets = asRecord(clinicalRules?.resolvedTargets);
  const candidates = [
    clientRecord.finalTargetCalories,
    clientRecord.targetCalories,
    clientRecord.conditionAdjustedTargetCalories,
    resolvedTargets?.finalTargetCalories,
    resolvedTargets?.targetCalories,
    resolvedTargets?.conditionAdjustedTargetCalories,
    goals.targetCalories,
    goals.eerGoalCalories,
    goalCalories?.value,
    goalCaloriesKcal?.value,
    clientRecord.eerOverride,
    clientRecord.eerCalories,
    clientRecord.maintenanceEER,
    client.eer,
  ];

  for (const candidate of candidates) {
    const value = finiteNumber(candidate);
    if (value != null && value > 0) return value;
  }
  return null;
}

function renderClinicalCalorieAdjustmentTooltip(info: ClientClinicalCalorieAdjustmentInfo): React.ReactNode {
  const bmiText = info.bmi != null ? `BMI ${formatNumber(info.bmi, 1)}` : 'BMI >= 25';
  const triggerText =
    info.conditionLabel === 'Clinical rule'
      ? 'A clinical energy rule adjusted the calorie base.'
      : `${info.conditionLabel} with ${bmiText} triggered the clinical energy rule.`;

  return (
    <Box sx={{ display: 'grid', gap: 0.65, maxWidth: 440 }}>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>
        Clinical Adjustment Applied
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', lineHeight: 1.45, color: '#fff' }}>
        {triggerText}
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', lineHeight: 1.45, color: '#fff' }}>
        Original EER: {formatMetric(info.originalCalories, 0)} kcal
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', lineHeight: 1.45, color: '#fff' }}>
        Adjusted EER: {formatMetric(info.adjustedCalories, 0)} kcal
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', lineHeight: 1.45, color: '#fff' }}>
        Reduction: {formatMetric(info.reductionCalories, 0)} kcal
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', lineHeight: 1.45, color: '#fff' }}>
        Percentage-based nutrient targets, including carbohydrates, use the adjusted EER.
      </Typography>
      {info.appliedRule ? (
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.7rem', lineHeight: 1.35, color: '#D8F3FA' }}>
          {info.appliedRule}
        </Typography>
      ) : null}
    </Box>
  );
}

function getResolvedClinicalTargets(goals: ClientGoalRecord): ClientGoalRecord {
  return asRecord(getClinicalRules(goals).resolvedTargets) ?? {};
}

function hasResolvedClinicalTarget(goals: ClientGoalRecord, key?: ClinicalTargetKey): boolean {
  if (!key) return false;
  return asRecord(getResolvedClinicalTargets(goals)[key]) !== null;
}

function getAppliedClinicalRules(goals: ClientGoalRecord): string[] {
  const clinicalRules = getClinicalRules(goals);
  const resolvedTargets = asRecord(clinicalRules.resolvedTargets);
  return uniqueStrings([
    ...getStringList(resolvedTargets?.appliedRules),
    ...getStringList(clinicalRules.appliedRules),
  ]);
}

function clinicalRuleMatchesTarget(rule: string, key: ClinicalTargetKey): boolean {
  const lower = rule.toLowerCase();
  switch (key) {
    case 'targetProtein':
      return lower.startsWith('protein:') || lower.includes('protein cap') || lower.includes(' protein ');
    case 'targetCarbs':
      return lower.startsWith('carbs:') || lower.startsWith('carbohydrate:') || lower.includes('carbohydrate');
    case 'targetFat':
      return lower.startsWith('fat:') || lower.includes('total fat');
    case 'targetSaturatedFat':
      return lower.startsWith('saturated fat:') || lower.includes('sfa') || lower.includes('saturated fat');
    case 'targetFibre':
      return lower.startsWith('fibre:') || lower.startsWith('fiber:') || lower.includes('fibre') || lower.includes('fiber');
    case 'targetSodium':
      return lower.startsWith('sodium:') || lower.includes('sodium');
    case 'targetFreeSugars':
      return lower.startsWith('free sugars:') || lower.includes('free sugars');
    default:
      return false;
  }
}

function getClinicalTargetRules(goals: ClientGoalRecord, key: ClinicalTargetKey): string[] {
  return getAppliedClinicalRules(goals).filter((rule) => clinicalRuleMatchesTarget(rule, key));
}

function formatClinicalReasons(clinicalRules: ClientGoalRecord): string {
  const conditions = getStringList(clinicalRules.matchedConditions);
  const biomarkers = getBiomarkerOverlayLabels(clinicalRules.biomarkerOverlays);
  const reasons = uniqueStrings([...conditions, ...biomarkers]);
  return reasons.length > 0 ? reasons.join(', ') : 'Clinical nutrition rules';
}

function renderClinicalTargetTooltip(
  row: TargetRow,
  goals: ClientGoalRecord,
  key: ClinicalTargetKey,
): React.ReactNode {
  const clinicalRules = getClinicalRules(goals);
  const rules = getClinicalTargetRules(goals, key);

  return (
    <Box sx={{ display: 'grid', gap: 0.65, maxWidth: 460 }}>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>
        Clinical Target Applied
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', lineHeight: 1.45, color: '#fff' }}>
        {row.label} target: {row.target}
      </Typography>
      {row.range ? (
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', lineHeight: 1.45, color: '#fff' }}>
          Clinical range: {row.range}
        </Typography>
      ) : null}
      {row.energy ? (
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', lineHeight: 1.45, color: '#fff' }}>
          Energy basis: {row.energy}
        </Typography>
      ) : null}
      <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.74rem', lineHeight: 1.45, color: '#fff' }}>
        Reason: {formatClinicalReasons(clinicalRules)}
      </Typography>
      {row.calculationExplanation ? (
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.7rem', lineHeight: 1.4, color: '#D8F3FA' }}>
          How this target was calculated: {row.calculationExplanation}
        </Typography>
      ) : null}
      {rules.length > 0 ? (
        <Box sx={{ display: 'grid', gap: 0.4 }}>
          {rules.map((rule) => (
            <Typography
              key={rule}
              sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.7rem', lineHeight: 1.35, color: '#D8F3FA' }}
            >
              Resolution rule: {rule}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

function withClinicalTargetTooltip(
  goals: ClientGoalRecord,
  key: ClinicalTargetKey | undefined,
  row: TargetRow,
): TargetRow {
  if (!key || !hasResolvedClinicalTarget(goals, key)) return row;
  return {
    ...row,
    note: row.note ?? 'Clinical adjustment applied',
    tooltip: renderClinicalTargetTooltip(row, goals, key),
  };
}

function buildDailyCalorieRow(client: Client, goals: ClientGoalRecord): TargetRow | null {
  const clinicalAdjustment = getClientClinicalCalorieAdjustmentInfo(client);
  if (clinicalAdjustment) {
    return {
      label: 'Daily Calories',
      target: `${formatMetric(clinicalAdjustment.adjustedCalories, 0)} kcal`,
      energy: `Original EER ${formatMetric(clinicalAdjustment.originalCalories, 0)} kcal`,
      range: `-${formatMetric(clinicalAdjustment.reductionCalories, 0)} kcal`,
      note: 'Clinical adjustment applied',
      tooltip: renderClinicalCalorieAdjustmentTooltip(clinicalAdjustment),
    };
  }

  const energy = resolveDailyCalorieTarget(client, goals);
  return energy != null && energy > 0
    ? { label: 'Daily Calories', target: `${formatMetric(energy, 0)} kcal` }
    : null;
}

function buildMacroRows(client: Client, goals: ClientGoalRecord): TargetRow[] {
  const targetCalories = resolveDailyCalorieTarget(client, goals);
  const fixedMacroExplanation = (label: string, percent: number, kcalPerGram: number): string | undefined => {
    if (targetCalories == null || targetCalories <= 0) return undefined;
    const kcal = Math.round(targetCalories * percent / 100);
    const grams = Math.round(kcal / kcalPerGram);
    return `Client Targets logic: ${label} is ${percent}% of the ${formatMetric(targetCalories, 0)} kcal/day energy target. Formula: (${percent}% × ${formatMetric(targetCalories, 0)} kcal/day) ÷ ${kcalPerGram} kcal/g. Base result: ${formatMetric(grams, 0)} g/day (${formatMetric(kcal, 0)} kcal/day).`;
  };
  const storedDrvExplanation = (
    nutrientIds: string[],
    fieldOrder: Array<'ul' | 'ai' | 'rda'>,
    label: string,
    kcalPerGram?: number,
  ): string | undefined => {
    const requirement = findDrvRequirementByNutrientIds(client.drvRequirements, nutrientIds);
    if (!requirement) return undefined;
    const source = String(requirement.source ?? client.drvRequirements?.source ?? 'selected guideline').replace(/_/g, ' ').toUpperCase();
    const lifestage = String(client.drvRequirements?.lifestageUsed ?? 'the client life stage');
    for (const field of fieldOrder) {
      const value = finiteNumber(requirement[field]);
      if (value == null) continue;
      const fieldLabel = field === 'ul' ? 'upper limit (UL)' : field === 'ai' ? 'adequate intake (AI)' : 'recommended intake (RDA/RNI)';
      const grams = finiteNumber(requirement[`${field}Grams`]);
      const isPercentEnergy = String(requirement.unit ?? '').includes('%');
      if (isPercentEnergy && grams != null && targetCalories != null && kcalPerGram) {
        return `Client Targets logic: use the ${source} ${fieldLabel} for ${lifestage}. Formula: (${formatNumber(value)}% × ${formatMetric(targetCalories, 0)} kcal/day) ÷ ${kcalPerGram} kcal/g. Base result: ${field === 'ul' ? '≤' : ''}${formatNumber(grams)} g/day.`;
      }
      return `Client Targets logic: use the ${source} ${label} ${fieldLabel} directly for ${lifestage}. Base result: ${field === 'ul' ? '≤' : ''}${formatNumber(value)} ${normalizeUnit(requirement.unit, '')}.`;
    }
    return undefined;
  };
  const rows: Array<TargetRow | null> = [
    buildDailyCalorieRow(client, goals),
    macroRow(goals, 'Protein', 'protein', 'g', 'targetProtein', fixedMacroExplanation('protein', 20, 4), targetCalories),
    macroRow(goals, 'Carbohydrate', 'carbohydrates', 'g', 'targetCarbs', fixedMacroExplanation('carbohydrate', 50, 4), targetCalories),
    macroRow(goals, 'Total Fat', 'totalFat', 'g', 'targetFat', fixedMacroExplanation('total fat', 30, 9), targetCalories),
    macroRow(
      goals,
      'Saturated Fat',
      'saturatedFat',
      'g',
      'targetSaturatedFat',
      storedDrvExplanation(['saturated_fat'], ['ul', 'rda', 'ai'], 'saturated fat', 9),
      targetCalories,
    ),
    scalarRow(
      goals,
      'Fibre',
      'dietaryFibre',
      'g',
      0,
      'targetFibre',
      storedDrvExplanation(['dietary_fibre', 'dietary_fiber', 'fiber'], ['ai', 'rda', 'ul'], 'fibre'),
    ),
    scalarRow(goals, 'Free Sugars', 'freeSugars', 'g', 0, 'targetFreeSugars'),
    scalarRow(goals, 'Sodium', 'sodium', 'mg', 0, 'targetSodium'),
    scalarRow(
      goals,
      'Water',
      'water',
      'L/day',
      1,
      undefined,
      storedDrvExplanation(['water_total', 'water', 'water_from_drinks'], ['ai', 'rda', 'ul'], 'water'),
    ),
  ];

  return rows.filter(Boolean) as TargetRow[];
}

function buildMicroRows(goals: ClientGoalRecord): MicroTargetRow[] {
  const micros = asRecord(goals.micronutrients);
  if (!micros) return [];
  const labelOverrides: Record<string, string> = {
    omega_3: 'Omega-3',
    omega_6: 'Omega-6',
    mufa: 'MUFA',
    pufa: 'PUFA',
    ala: 'ALA',
    la: 'LA',
    dha: 'DHA',
    epa: 'EPA',
  };

  return Object.entries(micros)
    .map(([key, raw]) => {
      const item = asRecord(raw) ?? {};
      const label = String(item.displayName ?? item.label ?? labelOverrides[key] ?? formatDisplayLabel(key));
      const value = finiteNumber(item.value);
      const unit = normalizeUnit(item.unit, '');
      const hasNumericTarget = value != null;
      const priorityOnly = item.priorityOnly === true || !hasNumericTarget;
      const target = value == null ? 'Priority' : `${formatNumber(value, 1)} ${unit}`.trim();
      const adjustedBy = getRuleTraces(item.adjustedBy);
      const priorityRuleTraces = getRuleTraces(item.priorityRules);
      const legacySources = priorityOnly ? [] : getStringList(item.sources);
      const valueSources = uniqueStrings([
        ...adjustedBy.map((rule) => rule.source),
        ...legacySources,
      ]);
      const valueRules = uniqueStrings(adjustedBy.map((rule) => rule.rule));
      const prioritySources = uniqueStrings([
        ...priorityRuleTraces.map((rule) => rule.source),
        ...getStringList(item.prioritySources),
      ]);
      const priorityRules = uniqueStrings(priorityRuleTraces.map((rule) => rule.rule));

      return { label, target, valueSources, valueRules, prioritySources, priorityRules, priorityOnly, hasNumericTarget };
    })
    .sort((a, b) => {
      if (a.hasNumericTarget !== b.hasNumericTarget) return a.hasNumericTarget ? -1 : 1;
      if (a.priorityOnly !== b.priorityOnly) return a.priorityOnly ? -1 : 1;
      return a.label.localeCompare(b.label);
    });
}

function getClinicalRules(goals: ClientGoalRecord): ClientGoalRecord {
  return asRecord(goals.clinicalRules) ?? {};
}

function getStringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((item) => String(item).trim()).filter(Boolean)
    : [];
}

function uniqueStrings(values: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

function getRuleTraces(value: unknown): Array<{ source: string; rule: string }> {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const record = asRecord(item);
      const source = String(record?.source ?? '').trim();
      const rule = String(record?.rule ?? '').trim();
      return source && rule ? { source, rule } : null;
    })
    .filter((item): item is { source: string; rule: string } => Boolean(item));
}

function getBiomarkerOverlayLabels(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      const row = asRecord(item);
      return String(row?.matchedBy ?? row?.biomarker ?? '').trim();
    })
    .filter(Boolean);
}

function renderRuleTooltip(rules: string[]): React.ReactNode {
  const visibleRules = rules.filter(Boolean);
  if (visibleRules.length === 0) return '';

  return (
    <Box sx={{ display: 'grid', gap: 0.5, maxWidth: 420 }}>
      {visibleRules.map((rule) => (
        <Typography
          key={rule}
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontSize: '0.75rem',
            lineHeight: 1.4,
            color: '#fff',
          }}
        >
          {rule}
        </Typography>
      ))}
    </Box>
  );
}

const sectionCardSx = {
  borderRadius: '16px',
  border: `1px solid ${colors.border}`,
  bgcolor: '#fff',
  p: { xs: 1.5, sm: 2, md: 2.25 },
  minWidth: 0,
} as const;

const tableHeaderSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.72rem',
  fontWeight: 700,
  lineHeight: 1.2,
  color: '#667085',
  textTransform: 'uppercase',
  letterSpacing: 0,
} as const;

const tableCellSx = {
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.8125rem',
  lineHeight: 1.35,
  color: '#344054',
  minWidth: 0,
  overflowWrap: 'anywhere',
} as const;

const chipSx = {
  height: 24,
  borderRadius: '6px',
  bgcolor: '#F3FBFE',
  color: '#0B89B7',
  border: '1px solid #B9E4F2',
  fontFamily: 'Inter, sans-serif',
  fontSize: '0.72rem',
  fontWeight: 600,
  '& .MuiChip-label': { px: 0.9 },
} as const;

export const ClientDetailsV2ClientTargetsTab: React.FC<ClientDetailsV2ClientTargetsTabProps> = ({
  client,
  onOpenClientGoalsEditor,
}) => {
  const goals = React.useMemo(() => asRecord(client.clientGoals) ?? {}, [client.clientGoals]);
  const macroRows = React.useMemo(() => buildMacroRows(client, goals), [client, goals]);
  const microRows = React.useMemo(() => buildMicroRows(goals), [goals]);
  const calorieTarget = resolveDailyCalorieTarget(client, goals);
  const macroGrams = ['protein', 'carbohydrates', 'totalFat'].map(key => finiteNumber(asRecord(asRecord(goals[key])?.grams)?.value));
  const calorieGap = calorieTarget != null && macroGrams.every(value => value != null)
    ? macroEnergyGap(calorieTarget, macroGrams[0]!, macroGrams[1]!, macroGrams[2]!) : null;
  const clinicalRules = React.useMemo(() => getClinicalRules(goals), [goals]);
  const matchedConditions = getStringList(clinicalRules.matchedConditions);
  const biomarkerLabels = getBiomarkerOverlayLabels(clinicalRules.biomarkerOverlays);

  return (
    <Box sx={{ mt: 2.25, display: 'grid', gap: 1.35, minWidth: 0 }}>
      <Paper
        elevation={0}
        sx={{
          border: '1px solid #B9E4F2',
          borderRadius: '12px',
          bgcolor: '#F3FBFE',
          px: 1.5,
          py: 1.15,
          display: 'grid',
          gap: 1,
          gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) auto' },
          alignItems: 'center',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.88rem',
              fontWeight: 700,
              color: '#0f172a',
              lineHeight: 1.25,
            }}
          >
            Client Targets
          </Typography>
        </Box>
        {onOpenClientGoalsEditor ? (
          <Button
            variant="outlined"
            size="small"
            onClick={(event) => onOpenClientGoalsEditor(event.currentTarget)}
            sx={{
              borderRadius: '10px',
              borderColor: '#20AEDC',
              color: '#0B89B7',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '0.78rem',
              textTransform: 'none',
              px: 1.5,
              whiteSpace: 'nowrap',
              '&:hover': { borderColor: '#128fbd', bgcolor: '#EAF8FC' },
            }}
          >
            Edit targets
          </Button>
        ) : null}
      </Paper>

      {(matchedConditions.length > 0 || biomarkerLabels.length > 0) && (
        <Paper elevation={0} sx={sectionCardSx}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', fontWeight: 700, color: '#0f172a' }}>
            Clinical matches
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {matchedConditions.map((condition) => (
              <Chip key={`condition-${condition}`} label={condition} size="small" sx={chipSx} />
            ))}
            {biomarkerLabels.map((biomarker) => (
              <Chip
                key={`biomarker-${biomarker}`}
                label={biomarker}
                size="small"
                sx={{ ...chipSx, bgcolor: '#F8FAFC', color: '#475467', borderColor: '#D0D5DD' }}
              />
            ))}
          </Box>
        </Paper>
      )}

      <Paper elevation={0} sx={sectionCardSx}>
        <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>
          Macro and Daily Targets
        </Typography>
        <Box sx={{ mt: 1.5, overflowX: 'auto' }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'minmax(160px, 1.2fr) minmax(120px, 0.8fr) minmax(120px, 0.8fr) minmax(120px, 0.8fr)',
              borderTop: `1px solid ${colors.border}`,
              minWidth: 620,
            }}
          >
            {calorieGap != null && Math.abs(calorieGap) > 9 && (
              <Typography role="status" sx={{ gridColumn: '1 / -1', color: '#B45309', py: 1 }}>
                Macro targets provide {Math.round((calorieTarget ?? 0) - calorieGap)} of {calorieTarget} kcal/day.
                {' '}Difference: {Math.round(Math.abs(calorieGap))} kcal. Review the targets and clinical limits.
              </Typography>
            )}
            {['Nutrient', 'Planning target', 'Energy', 'Clinical limit / range'].map((header) => (
              <Box key={header} sx={{ py: 0.85, px: 1, borderBottom: `1px solid ${colors.border}` }}>
                <Typography sx={tableHeaderSx}>{header}</Typography>
              </Box>
            ))}
            {macroRows.map((row) => (
              <React.Fragment key={row.label}>
                <Box sx={{ py: 1, px: 1, borderBottom: `1px solid ${colors.border}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                    <Typography sx={{ ...tableCellSx, fontWeight: 700, color: '#0f172a' }}>{row.label}</Typography>
                    {row.tooltip ? (
                      <Tooltip title={row.tooltip} placement="top" arrow enterDelay={150}>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#20AEDC',
                            cursor: 'help',
                            flex: '0 0 auto',
                          }}
                          aria-label={`${row.label} target calculation details`}
                        >
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </Box>
                      </Tooltip>
                    ) : null}
                  </Box>
                  {row.note ? <Typography sx={{ ...tableCellSx, mt: 0.25, fontSize: '0.74rem', color: '#667085' }}>{row.note}</Typography> : null}
                </Box>
                <Box sx={{ py: 1, px: 1, borderBottom: `1px solid ${colors.border}` }}>
                  <Typography sx={{ ...tableCellSx, fontWeight: 700, color: '#0B89B7', fontVariantNumeric: 'tabular-nums' }}>
                    {row.target}
                  </Typography>
                </Box>
                <Box sx={{ py: 1, px: 1, borderBottom: `1px solid ${colors.border}` }}>
                  <Typography sx={{ ...tableCellSx, fontVariantNumeric: 'tabular-nums' }}>{row.energy || '-'}</Typography>
                </Box>
                <Box sx={{ py: 1, px: 1, borderBottom: `1px solid ${colors.border}` }}>
                  <Typography sx={{ ...tableCellSx, fontVariantNumeric: 'tabular-nums' }}>{row.range || '-'}</Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={sectionCardSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.88rem', fontWeight: 700, color: '#0f172a' }}>
            Micronutrient Targets
          </Typography>
          <Tooltip title="Hover over the clinical chips to view the rule details." placement="top" arrow>
            <Box
              component="span"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#20AEDC',
                cursor: 'help',
              }}
              aria-label="Hover over the clinical chips to view the rule details."
            >
              <InfoOutlinedIcon sx={{ fontSize: 17 }} />
            </Box>
          </Tooltip>
        </Box>
        {microRows.length === 0 ? (
          <Typography sx={{ mt: 1, fontFamily: 'Inter, sans-serif', fontSize: '0.8rem', color: '#667085' }}>
            No micronutrient targets are currently stored for this client.
          </Typography>
        ) : (
          <Box sx={{ mt: 1.5, overflowX: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'minmax(180px, 1fr) minmax(120px, 0.55fr) minmax(220px, 1.45fr)',
                borderTop: `1px solid ${colors.border}`,
                minWidth: 640,
              }}
            >
              {['Nutrient', 'Target', 'Clinical rule'].map((header) => (
                <Box key={header} sx={{ py: 0.85, px: 1, borderBottom: `1px solid ${colors.border}` }}>
                  <Typography sx={tableHeaderSx}>{header}</Typography>
                </Box>
              ))}
              {microRows.map((row) => (
                <React.Fragment key={`${row.label}-${row.target}`}>
                  <Box sx={{ py: 1, px: 1, borderBottom: `1px solid ${colors.border}` }}>
                    <Typography sx={{ ...tableCellSx, fontWeight: 700, color: '#0f172a' }}>{row.label}</Typography>
                    {row.prioritySources.length > 0 && row.valueSources.length === 0 ? (
                      <Typography sx={{ ...tableCellSx, mt: 0.25, fontSize: '0.72rem', color: '#667085' }}>
                        Clinical priority
                      </Typography>
                    ) : null}
                  </Box>
                  <Box sx={{ py: 1, px: 1, borderBottom: `1px solid ${colors.border}` }}>
                    <Typography
                      sx={{
                        ...tableCellSx,
                        fontWeight: 700,
                        color: row.hasNumericTarget ? '#0B89B7' : '#667085',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {row.target}
                    </Typography>
                  </Box>
                  <Box sx={{ py: 0.75, px: 1, borderBottom: `1px solid ${colors.border}` }}>
                    <Box sx={{ display: 'grid', gap: 0.45 }}>
                      {row.valueSources.length > 0 || row.prioritySources.length > 0 ? (
                        <>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
                            {row.valueSources.map((source) => (
                              <Tooltip
                                key={`${row.label}-${source}`}
                                title={renderRuleTooltip(row.valueRules)}
                                placement="top"
                                arrow
                                enterDelay={150}
                              >
                                <Chip label={source} size="small" sx={{ ...chipSx, cursor: row.valueRules.length > 0 ? 'help' : 'default' }} />
                              </Tooltip>
                            ))}
                            {row.prioritySources
                              .filter((source) => !row.valueSources.some((valueSource) => valueSource.toLowerCase() === source.toLowerCase()))
                              .map((source) => (
                                <Tooltip
                                  key={`${row.label}-${source}-priority`}
                                  title={renderRuleTooltip(row.priorityRules)}
                                  placement="top"
                                  arrow
                                  enterDelay={150}
                                >
                                  <Chip
                                    label={source}
                                    size="small"
                                    sx={{
                                      ...chipSx,
                                      bgcolor: '#F8FAFC',
                                      color: '#475467',
                                      borderColor: '#D0D5DD',
                                      cursor: row.priorityRules.length > 0 ? 'help' : 'default',
                                    }}
                                  />
                                </Tooltip>
                              ))}
                          </Box>
                        </>
                      ) : (
                        <Typography sx={tableCellSx}>-</Typography>
                      )}
                    </Box>
                  </Box>
                </React.Fragment>
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Temporarily hidden: meal slot configuration on client details.
      <Paper
        elevation={0}
        sx={{
          ...sectionCardSx,
          p: 0,
          overflow: 'hidden',
          '& .shadow-lg, & .shadow-sm, & .shadow': { boxShadow: 'none' },
          '& > *': { border: 'none', borderRadius: 0, background: 'transparent' },
        }}
      >
        <MealTypesIntegrated
          clientId={client.id}
          clientName={[client.firstName, client.lastName].filter(Boolean).join(' ').trim() || undefined}
          client={client}
          maxMealSlots={5}
        />
      </Paper>
      */}
    </Box>
  );
};
